import { Star, ExternalLink, Loader2 } from "lucide-react";
import { useGoogleReviews } from "@/hooks/useGoogleReviews";
import { useLanguage } from "@/contexts/LanguageContext";

const COPY = {
  lv: {
    eyebrow: "Klientu atsauksmes",
    title: "Ko par mums saka klienti",
    based: (n: number) => `${n} Google atsauksmes`,
    leave: "Atstāt atsauksmi Google",
    all: "Skatīt visas atsauksmes",
    empty: "Atsauksmes pašlaik nav pieejamas.",
  },
  en: {
    eyebrow: "Customer reviews",
    title: "What our clients say",
    based: (n: number) => `${n} Google reviews`,
    leave: "Leave a Google review",
    all: "See all reviews",
    empty: "Reviews are currently unavailable.",
  },
  ru: {
    eyebrow: "Отзывы клиентов",
    title: "Что говорят наши клиенты",
    based: (n: number) => `${n} отзывов в Google`,
    leave: "Оставить отзыв в Google",
    all: "Смотреть все отзывы",
    empty: "Отзывы пока недоступны.",
  },
};

const Stars = ({ rating, className = "" }: { rating: number; className?: string }) => (
  <div className={`flex items-center gap-0.5 ${className}`} aria-label={`${rating} / 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${i <= Math.round(rating) ? "fill-gold text-gold" : "text-muted-foreground/40"}`}
      />
    ))}
  </div>
);

export const GoogleReviews = () => {
  const { language } = useLanguage();
  const t = COPY[language as keyof typeof COPY] || COPY.en;
  const { data, loading } = useGoogleReviews();

  if (loading) {
    return (
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </section>
    );
  }

  if (!data || !data.reviewUrl) return null;

  const reviews = (data.reviews || []).filter((r) => r.text?.trim()).slice(0, 6);

  return (
    <section className="py-20 lg:py-28 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="text-gold font-display text-xs tracking-[0.3em] uppercase">{t.eyebrow}</span>
          <h2 className="mt-3 font-display text-3xl lg:text-4xl">{t.title}</h2>

          {data.rating !== null && (
            <div className="mt-6 flex flex-col items-center gap-2">
              <div className="flex items-center gap-3">
                <span className="font-display text-4xl">{data.rating.toFixed(1)}</span>
                <Stars rating={data.rating} className="scale-125" />
              </div>
              <p className="text-sm text-muted-foreground">{t.based(data.total)}</p>
            </div>
          )}
        </div>

        {reviews.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {reviews.map((r, idx) => (
              <article
                key={`${r.author}-${idx}`}
                className="rounded-2xl border border-border bg-card p-6 flex flex-col gap-4"
              >
                <div className="flex items-center gap-3">
                  {r.photo ? (
                    <img
                      src={r.photo}
                      alt={r.author}
                      loading="lazy"
                      decoding="async"
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-sm font-medium">
                      {r.author.charAt(0)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{r.author}</p>
                    <p className="text-xs text-muted-foreground">{r.relativeTime}</p>
                  </div>
                </div>
                <Stars rating={r.rating} />
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-6">{r.text}</p>
              </article>
            ))}
          </div>
        ) : (
          <p className="text-center text-sm text-muted-foreground">{t.empty}</p>
        )}

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={data.reviewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3 bg-foreground text-background text-sm tracking-wide uppercase rounded-full hover:opacity-90 transition-opacity"
          >
            {t.leave}
            <ExternalLink className="w-4 h-4" />
          </a>
          {data.mapsUrl && (
            <a
              href={data.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 border border-border text-sm tracking-wide uppercase rounded-full hover:bg-muted transition-colors"
            >
              {t.all}
            </a>
          )}
        </div>
      </div>
    </section>
  );
};
